import { type Editor } from '@tiptap/core';
import { exitSuggestion, type SuggestionKeyDownProps, type SuggestionProps } from '@tiptap/suggestion';
import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useTrackAgentContextAdded,
  useTrackAgentContextOpened,
  type WorkspaceAgentContextAddedMethod,
} from '@air/analytics';

import {
  type WorkspaceAgentMentionDismissReason,
  type WorkspaceAgentMentionPopupRef,
  type WorkspaceAgentMentionPopupSessionProps,
} from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/mentions/WorkspaceAgentMentionPopup';
import { useWorkspaceAgentComposerWorkspaceId } from '~/components/v2/components/workspace/workspace-agent-panel/composer-host/WorkspaceAgentComposerWorkspaceProvider';
import {
  type WorkspaceAgentBoardTreeSearchResult,
  type WorkspaceAgentMentionableItem,
  type WorkspaceAgentMentionItem,
  type WorkspaceAgentMentionResultsBySource,
  type WorkspaceAgentMentionSearchResult,
  type WorkspaceAgentMentionSourceKey,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import { usePrivateWorkspaceAgentBoardTree } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/usePrivateWorkspaceAgentBoardTree';
import { useWorkspaceAgentMentionSourceSelection } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/useWorkspaceAgentMentionSourceSelection';
import {
  WORKSPACE_AGENT_MENTION_CHAR,
  WORKSPACE_AGENT_MENTION_CONTEXTS_KIT_MENU_ATTRIBUTE,
  WORKSPACE_AGENT_MENTION_LIST_ID,
  WORKSPACE_AGENT_MENTION_REOPEN_META,
  WORKSPACE_AGENT_MENTION_SEARCH_DEBOUNCE_MS,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionConstants';
import {
  DEFAULT_WORKSPACE_AGENT_MENTION_SOURCE_KEY,
  toWorkspaceAgentPillNodeAttrs,
  WORKSPACE_AGENT_MENTION_SOURCES,
  WORKSPACE_AGENT_MENTION_SOURCES_BY_KEY,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionSources';
import {
  WORKSPACE_AGENT_MENTION_SUGGESTION_PLUGIN_KEY,
  type WorkspaceAgentMentionSuggestionOptions,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionSuggestion';
import { usePrivateWorkspaceAgentAssetMentionSearch } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentAssetMentionSearch';
import { usePrivateWorkspaceAgentBrandKitMentionSearch } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentBrandKitMentionSearch';
import { findWorkspaceAgentPillIdByReference } from '~/components/v2/components/workspace/workspace-agent-panel/tiptap/findWorkspaceAgentPillIdByReference';
import { insertWorkspaceAgentPill } from '~/components/v2/components/workspace/workspace-agent-panel/tiptap/insertWorkspaceAgentPill';

/** `'no-matches'` is the picker closing itself; the rest are the user closing it. */
type WorkspaceAgentMentionCloseReason = WorkspaceAgentMentionDismissReason | 'no-matches';

/** A pick or Escape keeps focus in the composer; an outside press does not. */
type WorkspaceAgentMentionExitReason = WorkspaceAgentMentionCloseReason | 'pick';

type WorkspaceAgentMentionSuggestionSessionState = {
  isOpen: boolean;
  query: string;
};

const CLOSED_SESSION_STATE: WorkspaceAgentMentionSuggestionSessionState = {
  isOpen: false,
  query: '',
};

const EMPTY_NEXT_PAGE_NODE_IDS: ReadonlySet<string> = new Set();

const EMPTY_BOARD_LIST_QUERY_SPECS: WorkspaceAgentBoardTreeSearchResult['listQuerySpecs'] = [];

const noopFetchNextPageForNode = () => undefined;

const noopListSnapshot: WorkspaceAgentBoardTreeSearchResult['onListSnapshot'] = () => undefined;

export type UsePrivateWorkspaceAgentMentionSuggestionStateParams = {
  areMentionsEnabled?: boolean;
  isCreditsDepleted?: boolean;
  /** When set (e.g. skill form), overrides `WorkspaceAgentComposerWorkspaceProvider`. */
  workspaceId?: string;
};

export type UsePrivateWorkspaceAgentMentionSuggestionStateResult = {
  /** Combobox ARIA for the contenteditable — the listbox never takes focus. */
  editorAriaAttributes: Record<string, string>;
  /** TipTap suggestion caret rect while the picker is open. */
  getSuggestionClientRect: () => DOMRect | null;
  /** Types an `@` so the plugin opens the picker itself. */
  openManualPicker: (editor: Editor | null | undefined) => void;
  popupProps: WorkspaceAgentMentionPopupSessionProps | null;
  /** The composer's `handleKeyDown` must consult this before Enter-to-send. */
  popupRef: RefObject<WorkspaceAgentMentionPopupRef>;
  suggestionOptions: WorkspaceAgentMentionSuggestionOptions;
};

/**
 * Owns the picker's data: active source, its search results, and the `@tiptap/suggestion` session.
 * The popup takes props in, and reports keys and the highlighted row back via `popupRef` / `onActiveOptionChange`.
 */
export const usePrivateWorkspaceAgentMentionSuggestionState = ({
  areMentionsEnabled = true,
  isCreditsDepleted = false,
  workspaceId: workspaceIdOverride,
}: UsePrivateWorkspaceAgentMentionSuggestionStateParams): UsePrivateWorkspaceAgentMentionSuggestionStateResult => {
  const isCreditsDepletedRef = useRef(isCreditsDepleted);
  isCreditsDepletedRef.current = isCreditsDepleted;
  const areMentionsEnabledRef = useRef(areMentionsEnabled);
  areMentionsEnabledRef.current = areMentionsEnabled;

  const workspaceIdFromComposer = useWorkspaceAgentComposerWorkspaceId();
  const workspaceId = workspaceIdOverride ?? workspaceIdFromComposer;
  const hasWorkspaceId = Boolean(workspaceId);
  const mentionSearchWorkspaceId = workspaceId ?? '';
  const { trackAgentContextAdded } = useTrackAgentContextAdded();
  const { trackAgentContextOpened } = useTrackAgentContextOpened();
  const contextOpenMethodRef = useRef<'button' | 'keyboard'>('keyboard');
  const contextAddMethodRef = useRef<WorkspaceAgentContextAddedMethod>('button');
  const [session, setSession] = useState<WorkspaceAgentMentionSuggestionSessionState>(CLOSED_SESSION_STATE);
  const [activeOptionId, setActiveOptionId] = useState<string | null>(null);
  const { activeSourceKey, autoSwitchToSourceWithHits, isSourcePinned, onSourceChange, resetForNewMention } =
    useWorkspaceAgentMentionSourceSelection({ isPickerOpen: session.isOpen, query: session.query });
  const popupRef = useRef<WorkspaceAgentMentionPopupRef>(null);
  /** The live `@tiptap/suggestion` session: its editor, its range, and its insert command. */
  const sessionRef = useRef<SuggestionProps<WorkspaceAgentMentionItem>>();
  const exitReasonRef = useRef<WorkspaceAgentMentionExitReason>();
  const hadEditorFocusOnStartRef = useRef<boolean>();
  /**
   * Escape's dismissal reopens on the next edit; the picker's own must stay closed for that `@`.
   * No initializer, so the flag is genuinely `boolean | undefined` rather than narrowed to `false`.
   */
  const hasAutoClosedRef = useRef<boolean>();
  /** What `openManualPicker` typed for the user, so Escape can take it back. */
  const manualInsertionRef = useRef<{ from: number; text: string }>();

  // Rules of hooks forbid iterating the registry, so every source's hook is called here in a fixed
  // order — a new source is a registry entry plus one call. A typed query enables every source, so
  // the picker can auto-switch to a stronger match — but once the user has pinned a chip there
  // is no switch left to make, and the source they did not pick has nothing to answer for.
  const isSourceEnabled = (sourceKey: WorkspaceAgentMentionSourceKey) =>
    session.isOpen && (activeSourceKey === sourceKey || (session.query !== '' && !isSourcePinned));

  const boards = usePrivateWorkspaceAgentBoardTree({
    enabled: hasWorkspaceId && isSourceEnabled('boards'),
    // The session, not `enabled`: what the user opened in the tree survives a trip to another chip
    // and is only given up when the picker itself closes.
    isPickerOpen: session.isOpen,
    query: session.query,
    workspaceId: mentionSearchWorkspaceId,
  });
  const assets = usePrivateWorkspaceAgentAssetMentionSearch({
    enabled: hasWorkspaceId && isSourceEnabled('assets'),
    query: session.query,
    workspaceId: mentionSearchWorkspaceId,
  });
  const brandKit = usePrivateWorkspaceAgentBrandKitMentionSearch({
    enabled: hasWorkspaceId && isSourceEnabled('brand-kit'),
    // The kit list, not the rows: whether the chip renders at all is decided for the whole picker.
    isPickerOpen: session.isOpen,
    query: session.query,
    workspaceId: mentionSearchWorkspaceId,
  });
  const resultsBySource = useMemo<WorkspaceAgentMentionResultsBySource>(
    () => ({ assets, boards, 'brand-kit': brandKit }),
    [assets, boards, brandKit],
  );

  // No brand kits in the workspace, no Brand kit chip — an empty tab is not worth a place in the row.
  const sources = useMemo(
    () =>
      WORKSPACE_AGENT_MENTION_SOURCES.filter((source) => source.key !== 'brand-kit' || brandKit.brandKits.length > 0),
    [brandKit.brandKits.length],
  );

  const activeSearchResult: WorkspaceAgentMentionSearchResult = resultsBySource[activeSourceKey];
  const activeSource = WORKSPACE_AGENT_MENTION_SOURCES_BY_KEY[activeSourceKey];
  /** Only the boards tree opens and closes, so only it answers for a row's state or its pending count. */
  const activeTreeResult = activeSource.listRole === 'tree' ? boards : undefined;

  /**
   * Every source that has reported back has nothing to offer, and is not about to change its mind:
   * still fetching, still stale or failed all keep the picker open. A source that never ran — the
   * inactive chip, or any of them before the session's workspace id resolves — reports an empty, settled
   * result too, so at least one must have actually looked before this can close anything.
   */
  const hasSettledWithoutMatches = useMemo(() => {
    const results = Object.values(resultsBySource);

    return (
      results.some((result) => result.hasLooked) &&
      results.every(
        (result) =>
          result.items.length === 0 && !result.isError && !result.isFetching && !result.isLoading && !result.isStale,
      )
    );
  }, [resultsBySource]);

  // The results the switch is about are only known after the queries above, so the effect is here
  // while every rule about when a switch is allowed lives in the selection hook.
  useEffect(() => {
    autoSwitchToSourceWithHits(resultsBySource);
  }, [autoSwitchToSourceWithHits, resultsBySource]);

  // A chip can go out from under the picker — the Brand kit one leaves with the workspace's last kit —
  // and the popup reads its copy off the active source's chip, so it would have none to read.
  useEffect(() => {
    if (session.isOpen && !sources.some((source) => source.key === activeSourceKey)) {
      onSourceChange(DEFAULT_WORKSPACE_AGENT_MENTION_SOURCE_KEY);
    }
  }, [activeSourceKey, onSourceChange, session.isOpen, sources]);

  const closeSession = useCallback(() => {
    sessionRef.current = undefined;
    manualInsertionRef.current = undefined;
    setActiveOptionId(null);
    resetForNewMention();
    setSession(CLOSED_SESSION_STATE);
  }, [resetForNewMention]);

  /** Dismissal goes through the plugin, so the same `@` does not re-open on the next keystroke. */
  const dismissPicker = useCallback(
    (reason: WorkspaceAgentMentionCloseReason) => {
      const editor = sessionRef.current?.editor;

      exitReasonRef.current = reason;
      hasAutoClosedRef.current = reason === 'no-matches';

      if (!editor || editor.isDestroyed) {
        closeSession();
        return;
      }

      // Escape on a button-opened picker takes back the `@` the button typed — only while untouched;
      // once the user typed after it, the text is theirs.
      const manualInsertion = manualInsertionRef.current;
      const range = sessionRef.current?.range;
      const insertionEnd = manualInsertion ? manualInsertion.from + manualInsertion.text.length : 0;
      const isUntouchedInsertion =
        !!manualInsertion &&
        !!range &&
        range.to === insertionEnd &&
        editor.state.doc.textBetween(manualInsertion.from, insertionEnd) === manualInsertion.text;

      if (reason === 'escape' && manualInsertion && isUntouchedInsertion) {
        editor.chain().deleteRange({ from: manualInsertion.from, to: insertionEnd }).run();
      }

      exitSuggestion(editor.view, WORKSPACE_AGENT_MENTION_SUGGESTION_PLUGIN_KEY);
    },
    [closeSession],
  );

  /**
   * A query that runs past a space is a mention being spelled out (`@Spring campaign`) right up until
   * it isn't, and then only the user could close the picker — a whole sentence typed after an
   * abandoned `@` would sit under an open popup. So once a multi-word query has come back settled and
   * empty everywhere, the picker bows out and the text the user kept typing is prose.
   *
   * Giving up is terminal for that `@`, so it waits out one more debounce interval of silence first:
   * `@Q3 report d` is momentarily a zero-hit while the user is still spelling `design`, and any
   * change to the query cancels the timer and starts the wait over.
   */
  useEffect(() => {
    if (!session.isOpen || !/\s/.test(session.query) || !hasSettledWithoutMatches) {
      return;
    }

    const idleTimeout = window.setTimeout(() => {
      dismissPicker('no-matches');
    }, WORKSPACE_AGENT_MENTION_SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(idleTimeout);
  }, [dismissPicker, hasSettledWithoutMatches, session.isOpen, session.query]);

  const openManualPicker = useCallback((editor: Editor | null | undefined) => {
    if (isCreditsDepletedRef.current || !areMentionsEnabledRef.current || !editor || editor.isDestroyed) {
      return;
    }

    // The plugin is the only honest "already open" check: Radix dismisses on `pointerdown`, before
    // this `click`, so hook state is already torn down and a second `@` would land in the message.
    if (WORKSPACE_AGENT_MENTION_SUGGESTION_PLUGIN_KEY.getState(editor.state)?.active) {
      return;
    }

    contextOpenMethodRef.current = 'button';

    // Asking for the picker outranks the picker having given up: the button is the one way back to
    // an `@` it auto-closed, and without this it would re-evaluate that `@` into the same refusal.
    hasAutoClosedRef.current = false;

    // `view.focus()` first, never a focus command — see `insertWorkspaceAgentPill` for the race.
    editor.view.focus();

    // Inserting over a `NodeSelection` (a clicked chip) would delete the chip; collapse to the end first.
    if (!editor.state.selection.empty) {
      editor.commands.setTextSelection(editor.state.selection.to);
    }

    const { from } = editor.state.selection;
    const characterBefore = editor.state.doc.textBetween(Math.max(from - 1, 0), from);
    const characterAfter = editor.state.doc.textBetween(from, Math.min(from + 1, editor.state.doc.content.size));

    // An `@` from a dismissed picker is already under the caret: re-evaluate it rather than add a second.
    if (characterBefore === WORKSPACE_AGENT_MENTION_CHAR) {
      // Not the button's `@` to take back on Escape: this branch cannot tell a typed one from it.
      manualInsertionRef.current = undefined;
      // Meta and not a bare transaction: nothing else may reopen a dismissed `@` without an edit.
      editor.view.dispatch(editor.state.tr.setMeta(WORKSPACE_AGENT_MENTION_REOPEN_META, true));

      return;
    }

    // After a word, prefix a space; mid-word, a bare `@` rather than splitting the user's word.
    const isAfterWord = !!characterBefore && !/\s/.test(characterBefore);
    const isAtWordEnd = !characterAfter || /\s/.test(characterAfter);
    const insertedText = isAfterWord && isAtWordEnd ? ` ${WORKSPACE_AGENT_MENTION_CHAR}` : WORKSPACE_AGENT_MENTION_CHAR;

    editor.chain().insertContent(insertedText).scrollIntoView().run();
    manualInsertionRef.current = { from, text: insertedText };
  }, []);

  // The editor belongs to the picker. Dismissing on an editor press leaves the plugin holding
  // `dismissedRange`, so the picker never returns for that `@`.
  const shouldIgnoreOutsidePress = useCallback((target: Node) => {
    const editorElement = sessionRef.current?.editor.view.dom;

    if (editorElement?.contains(target)) {
      return true;
    }

    if (target instanceof Element && target.closest(`[${WORKSPACE_AGENT_MENTION_CONTEXTS_KIT_MENU_ATTRIBUTE}]`)) {
      return true;
    }

    return false;
  }, []);

  const handleSelect = useCallback((item: WorkspaceAgentMentionableItem, method: WorkspaceAgentContextAddedMethod) => {
    contextAddMethodRef.current = method;
    exitReasonRef.current = 'pick';
    sessionRef.current?.command(item);
  }, []);

  /** The kit menu is the only control in the popup that takes focus; closing it gives the caret back. */
  const handleRefocusEditor = useCallback(() => {
    const editor = sessionRef.current?.editor;

    if (editor && !editor.isDestroyed) {
      editor.view.focus();
    }
  }, []);

  const handleSuggestionSession = useCallback(
    (props: SuggestionProps<WorkspaceAgentMentionItem>) => {
      if (isCreditsDepletedRef.current) {
        exitSuggestion(props.editor.view, WORKSPACE_AGENT_MENTION_SUGGESTION_PLUGIN_KEY);

        return;
      }

      if (!sessionRef.current) {
        trackAgentContextOpened({ method: contextOpenMethodRef.current });
        contextOpenMethodRef.current = 'keyboard';
        hadEditorFocusOnStartRef.current = props.editor.view.hasFocus();
        exitReasonRef.current = undefined;
        // A fresh `@` is a fresh chance; only the auto-closed one it left behind stays dismissed.
        hasAutoClosedRef.current = false;
      }

      sessionRef.current = props;
      setSession({
        isOpen: true,
        query: props.query,
      });
    },
    [trackAgentContextOpened],
  );

  const handleSuggestionExit = useCallback(() => {
    const editor = sessionRef.current?.editor;
    const exitReason = exitReasonRef.current;
    const shouldRefocus =
      exitReason === 'pick' || exitReason === 'escape' || (!exitReason && !!hadEditorFocusOnStartRef.current);

    closeSession();

    if (!shouldRefocus || !editor || editor.isDestroyed) {
      return;
    }

    // `EditorView.destroy()` runs plugin `destroy` (this exit) before TipTap flips `isDestroyed`, so
    // an unmounting composer looks alive here; a detached DOM node is the reliable tell.
    if (!editor.view.dom.isConnected) {
      return;
    }

    editor.view.focus();
  }, [closeSession]);

  const suggestionOptions = useMemo<WorkspaceAgentMentionSuggestionOptions>(
    () => ({
      allow: () => areMentionsEnabledRef.current && !isCreditsDepletedRef.current,

      command: ({ editor, props: item }) => {
        // A library heads a section of the board tree; it is not something a pill can reference.
        if (item.kind === 'library') {
          return;
        }

        if (item.kind === 'asset' || item.kind === 'board') {
          trackAgentContextAdded({ method: contextAddMethodRef.current, type: item.kind });
        }

        // The ref, not the closure: React can still hold a prior session's `command` with a stale range.
        const range = sessionRef.current?.range;

        if (!range) {
          return;
        }

        const attrs = toWorkspaceAgentPillNodeAttrs(item);
        // Mentioning the same entity twice reuses the pill id: two monikers, one pill on the wire.
        const existingPillId = findWorkspaceAgentPillIdByReference(editor.state.doc, attrs);

        insertWorkspaceAgentPill({
          attrs: existingPillId ? { ...attrs, id: existingPillId } : attrs,
          editor,
          range,
        });
      },

      // Rows come from React Query, not the plugin: an async `items` would race the debounce.
      items: () => [],

      /**
       * A dismissed `@` stays dismissed until the document changes or the `@` button asks for it
       * back. Focus, blur and selection-only transactions never reopen it: the `@` still matches
       * under an unmoved caret, so any of them reopening it would cost an outside press a second
       * click — the first one's mousedown blurs the editor — and would undo Escape on the next
       * click anywhere.
       *
       * On an edit, Escape's dismissal does give the same `@` back, but a picker that gave up on it
       * does not — until the query is back to one word. Backspacing `@spring campaign` to `@spring`
       * is the user walking the mention back, and the reason the picker bowed out is gone with the space.
       */
      shouldResetDismissed: ({ match, transaction }) => {
        if (!transaction.docChanged) {
          return transaction.getMeta(WORKSPACE_AGENT_MENTION_REOPEN_META) === true;
        }

        const isStillAutoClosed = !!hasAutoClosedRef.current && /\s/.test(match.query);

        hasAutoClosedRef.current = isStillAutoClosed;

        return !isStillAutoClosed;
      },

      render: () => ({
        onExit: handleSuggestionExit,
        onKeyDown: ({ event }: SuggestionKeyDownProps) => popupRef.current?.onKeyDown(event) ?? false,
        onStart: handleSuggestionSession,
        onUpdate: handleSuggestionSession,
      }),
    }),
    [handleSuggestionExit, handleSuggestionSession, trackAgentContextAdded],
  );

  const popupProps = useMemo<WorkspaceAgentMentionPopupSessionProps | null>(() => {
    if (!session.isOpen) {
      return null;
    }

    return {
      activeSourceKey,
      brandKits: brandKit.brandKits,
      hasNextPage: activeSearchResult.hasNextPage,
      hasNextPageNodeIds: activeTreeResult?.hasNextPageNodeIds ?? EMPTY_NEXT_PAGE_NODE_IDS,
      isError: activeSearchResult.isError,
      isFetching: activeSearchResult.isFetching,
      isLoading: activeSearchResult.isLoading,
      isStale: activeSearchResult.isStale,
      items: activeSearchResult.items,
      listQuerySpecs: activeTreeResult?.listQuerySpecs ?? EMPTY_BOARD_LIST_QUERY_SPECS,
      onActiveOptionChange: setActiveOptionId,
      onDismiss: dismissPicker,
      onFetchNextPage: activeSearchResult.fetchNextPage,
      onFetchNextPageForNode: activeTreeResult?.fetchNextPageForNode ?? noopFetchNextPageForNode,
      onListSnapshot: activeTreeResult?.onListSnapshot ?? noopListSnapshot,
      onRefocusEditor: handleRefocusEditor,
      onRetry: activeSearchResult.refetch,
      onSelect: handleSelect,
      onSelectBrandKit: brandKit.selectBrandKit,
      onSourceChange,
      onToggleExpanded: activeTreeResult?.toggleExpanded,
      pendingChildCountNodeIds: activeTreeResult?.pendingChildCountNodeIds,
      query: session.query,
      selectedBrandKitId: brandKit.selectedBrandKitId,
      shouldIgnoreOutsidePress,
      sources,
      workspaceId,
    };
  }, [
    activeSearchResult,
    activeSourceKey,
    activeTreeResult,
    brandKit.brandKits,
    brandKit.selectBrandKit,
    brandKit.selectedBrandKitId,
    dismissPicker,
    handleRefocusEditor,
    handleSelect,
    onSourceChange,
    session,
    shouldIgnoreOutsidePress,
    sources,
    workspaceId,
  ]);

  // Must name the popup's actual role, which the source decides — the boards tree is not a listbox.
  const mentionPopupHasPopup = activeSource.listRole;

  const editorAriaAttributes = useMemo<Record<string, string>>(() => {
    const attributes: Record<string, string> = {
      'aria-autocomplete': 'list',
      'aria-expanded': String(session.isOpen),
      'aria-haspopup': mentionPopupHasPopup,
      role: 'combobox',
    };

    // Only while the picker is mounted: `aria-controls` pointing at an id that is not in the
    // document is a broken reference, not an idle one.
    if (session.isOpen) {
      attributes['aria-controls'] = WORKSPACE_AGENT_MENTION_LIST_ID;
    }

    if (activeOptionId) {
      attributes['aria-activedescendant'] = activeOptionId;
    }

    return attributes;
  }, [activeOptionId, mentionPopupHasPopup, session.isOpen]);

  const getSuggestionClientRect = useCallback(() => sessionRef.current?.clientRect?.() ?? null, []);

  return {
    editorAriaAttributes,
    getSuggestionClientRect,
    openManualPicker,
    popupProps,
    popupRef,
    suggestionOptions,
  };
};
